import React, { useEffect, useState, useCallback } from "react";
import UploadStatisticsChart from "./Charts/UploadStatisticsChart";
import ResourceStatusChart from "./Charts/ResourceStatusChart";
import RecentPublishedList from "./Recents/RecentPublishedList";
import RecentUploadList from "./Recents/RecentUploadList";
import TopDownloadList from "./Recents/TopDownloadList";
import RecentSearchList from "./Recents/RecentSearchList";
import FileReport from "./Recents/FileReport";
import TopUsersList from "./Recents/TopUsersList";
import RecentUsersList from "./Recents/RecentUsersList";
import SearchVolumeWidget from "./SearchVolumeWidget";
import TrendingQueriesWidget from "./TrendingQueriesWidget";
import SearchEffectivenessWidget from "./SearchEffectivenessWidget";
import FilesStatisticsChart from "./Charts/FilesStatisticsChart";
import UsersStatisticsChart from "./Recents/UsersStatisticsChart";

import userAPI from "../../api/userAPI"; // Import userAPI
import reportAPI from "../../api/reportAPI"; // Import reportAPI
import AuthLayout from "../../components/AuthLayout/AuthLayout";
//import ResourceStatistics from "./Charts/ResourceStatistics";

import { handleApiError, createErrorHandler } from "../../utils/notifications";
import { ErrorMessage, LoadingSpinner } from "../../components/common/ErrorComponents";

const Home = () => {
  const [uploadData, setUploadData] = useState([]);
  const [searchData, setSearchData] = useState([]);
  const [userStatData, setUserStatData] = useState([]);
  const [topUserStatData, setTopUserStatData] = useState([]);
  const [downloadData, setDownloadData] = useState([]);
  const [recentData, setRecentData] = useState([]);
  const [recentPublishData, setRecentPublishData] = useState([]);
  const [fileStatData, setFileStatData] = useState([]);
  const [statusData, setStatusData] = useState([]); // State for PieChart data
  const [fileReportData, setFileReportData] = useState({
    totalFiles: 0,
    largeFiles: 0,
    emptySizeFiles: 0,
    filesByStorage: [], // Add this missing property
  });

  // Error states for different sections
  const [dashboardError, setDashboardError] = useState(null);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [recentUsersData, setRecentUsersData] = useState([]);
  
  // Create stable error handlers with useCallback to prevent regeneration
  const handleDashboardError = useCallback(
    createErrorHandler('load dashboard data'),
    []
  );
  const handleUploadError = useCallback(
    createErrorHandler('load upload statistics'),
    []
  );
  const handleUserStatsError = useCallback(
    createErrorHandler('load user statistics'),
    []
  );
  const handleRecentDataError = useCallback(
    createErrorHandler('load recent data'),
    []
  );

  const getUploadStat = useCallback(async () => {
    try {
      let data = [];
      //get resources report data
      const response = await reportAPI.getUploadedStats();
      response.forEach((value, index) => {
        data.push({
          date: value._id,
          Total: value.total,
          Active: value.active,
          Deleted: value.deleted
        });
      });
      setUploadData(data);
    } catch (error) {
      handleUploadError(error);
    }
  }, []);

  const getUsersStat = useCallback(async () => {
    try {
      let data = [];
      //get resources report data
      const response = await reportAPI.getUsersStats();
      response.forEach((value, index) => {
        data.push({
          date: value._id,
          Total: value.total
        });
      });
      setUserStatData(data);
    } catch (error) {
      handleUserStatsError(error);
    }
  }, []);

  const getTopUsersStat = useCallback(async () => {
    try {
      //get resources report data
      const response = await reportAPI.getTopUsersStats(5);
      setTopUserStatData(response);
    } catch (error) {
      handleUserStatsError(error);
    }
  }, []);

  const getTopDownload = useCallback(async () => {
    try {
      //get resources report data
      const response = await reportAPI.getDownloadedStats(6);
      setDownloadData(response);
    } catch (error) {
      handleDashboardError(error);
    }
  }, []);

  const getRecentUpload = useCallback(async () => {
    try {
      //get resources report data
      const response = await reportAPI.getRecentResources("pending,draft", 6);
      setRecentData(response.items);
    } catch (error) {
      handleRecentDataError(error);
    }
  }, []);

  const getRecentPublish = useCallback(async () => {
    try {
      //get resources report data
      const response = await reportAPI.getRecentPublishedResources(6);
      setRecentPublishData(response.items);
    } catch (error) {
      handleRecentDataError(error);
    }
  }, []);

  const getRecentSearch = useCallback(async () => {
    try {
      //get resources report data
      const response = await reportAPI.getRecentSearches();
      setSearchData(response);
    } catch (error) {
      handleRecentDataError(error);
    }
  }, []);

  const getFileStat = useCallback(async () => {
    try {
      let data = [];
      //get resources report data
      const response = await reportAPI.getFileFormatStats();
      response.forEach((value, index) => {
        data.push({
          Name: value._id,
          Total: value.total,
        });
      });
      setFileStatData(data);
    } catch (error) {
      handleDashboardError(error);
    }
  }, []);

  const getRecentUsers = useCallback(async () => {
    try {
      // Fetch the last 5 recently registered users
      const response = await userAPI.getAllUsers({ page: 1, pageSize: 5, sortby: "created_at", direction: "desc" });
      setRecentUsersData(response.items || []); // Update state with fetched users
    } catch (error) {
      handleRecentDataError(error);
    }
  }, []);

  const getStatusReport = useCallback(async () => {
    try {
      // Fetch data from the /resources/status-report route
      const response = await reportAPI.getResourceStatusReport();
      if (response && Array.isArray(response)) {
        // Ensure the data structure matches the expected format
        const formattedData = response.map((item) => ({
          Name: item.status, // Match the "Name" key used in FileStat
          Total: item.total, // Match the "Total" key used in FileStat
        }));
        setStatusData(formattedData); // Update state with formatted data
      } else {
        throw new Error("Invalid data format for status report");
      }
    } catch (error) {
      handleDashboardError(error);
    }
  }, []);

  const getFileReport = useCallback(async () => {
    try {
      // Fetch data from the /files/report route
      const response = await reportAPI.getFileReport();
      setFileReportData(response); // Update state with fetched data
    } catch (error) {
      handleDashboardError(error);
    }
  }, []);

  useEffect(() => {
    let isComponentMounted = true;
    let map = null;

    const initializeDashboard = async () => {
      try {
        setLoadingDashboard(true);
        setDashboardError(null);

        // Import required AmCharts modules
        const am4core = await import('@amcharts/amcharts4/core');
        const am4maps = await import('@amcharts/amcharts4/maps');
        const am4geodata_worldLow = await import('@amcharts/amcharts4-geodata/worldLow');
        
        if (!isComponentMounted) return;

        // Initialize map
        map = am4core.create("chartdiv", am4maps.MapChart);
        map.geodata = am4geodata_worldLow.default;
        map.projection = new am4maps.projections.Miller();

        // Fetch all statistics data
        await Promise.all([
          getUploadStat(),
          getUsersStat(),
          getTopUsersStat(),
          getTopDownload(),
          getFileStat(),
          getRecentUpload(),
          getRecentPublish(),
          getRecentSearch(),
          getRecentUsers(),
          getStatusReport(),
          getFileReport()
        ]);

      } catch (error) {
        if (isComponentMounted) {
          setDashboardError(handleApiError(error, 'Failed to load dashboard'));
        }
      } finally {
        if (isComponentMounted) {
          setLoadingDashboard(false);
        }
      }
    };

    initializeDashboard();

    // Cleanup function
    return () => {
      isComponentMounted = false;
      if (map) {
        map.dispose();
        map = null;
      }
    };
  }, []); // Empty dependency array - this effect only runs once

  const retryLoadDashboard = () => {
    // Re-run the useEffect logic
    window.location.reload();
  };

  // Show loading state
  if (loadingDashboard) {
    return (
      <AuthLayout>
        <LoadingSpinner message="Loading dashboard data..." />
      </AuthLayout>
    );
  }

  // Show error state
  if (dashboardError) {
    return (
      <AuthLayout>
        <ErrorMessage 
          message={dashboardError} 
          onRetry={retryLoadDashboard}
          className="m-4"
        />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="row py-4 gx-4 gy-4">
          {/* Search Analytics Widgets */}
          <div className="col-4 mb-4">
            <SearchVolumeWidget />
          </div>
          <div className="col-4 mb-4">
            <TrendingQueriesWidget />
          </div>
          <div className="col-4 mb-4">
            <SearchEffectivenessWidget />
          </div>
          <div className="col-8 mb-4">
            <UploadStatisticsChart data={uploadData} />
          </div>
          <div className="col-4 mb-4">
            <FilesStatisticsChart fileStatData={fileStatData} />
          </div>
          <div className="col-8 mb-4">
            <UsersStatisticsChart userStatData={userStatData} />
          </div>
          <div className="col-4 mb-4">
            <ResourceStatusChart data={statusData} />
          </div>
          
          
          <div className="col-3 mb-4">
            <RecentUsersList recentUsersData={recentUsersData} />
          </div>
          <div className="col-3 mb-4">
            <TopUsersList topUserStatData={topUserStatData} />
          </div>
          <div className="col-3 mb-4">
            <RecentSearchList searchData={searchData} />
          </div>
          <div className="col-3 mb-4">
            <FileReport fileReportData={fileReportData} />
          </div>
          <div className="col-4 mb-4">
            <RecentUploadList recentData={recentData} />
          </div>
          <div className="col-4 mb-4">
            <RecentPublishedList recentPublishData={recentPublishData} />
          </div>
          <div className="col-4 mb-4">
            <TopDownloadList downloadData={downloadData} />
          </div>
          {/* <div className="col-12">
            <ResourceStatistics />
          </div> */}
      </div>
    </AuthLayout>
  );
};

export default Home;
